import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SincronizacionSapService } from '../services/sincronizacion-sap.service';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { PeriodosmetasService, PeriodoMetas } from '../../periodosmetas/services/periodosmetas.service';
import { showSpinner, hideSpinner } from 'src/app/core/services/functions.service';
import { finalize } from 'rxjs/operators';

export interface ProcessStatus {
    status: 'not_processed' | 'processing' | 'processed' | 'error';
    message: string;
}

@Component({
    selector: 'app-sincronizacionsap',
    templateUrl: './sincronizacionsap.component.html',
    styleUrls: ['./sincronizacionsap.component.scss']
})
export class SincronizacionSapComponent implements OnInit {
    form: FormGroup;
    isLoading = false;
    currentStep = 0;
    periodosAbiertos: PeriodoMetas[] = [];
    loadingStates = {
        vendedores: false,
        comisiones: false,
        comisionesJefes: false,
        ausentismos: false,
        etl: false
    };
    // Estado inicial del ETL
    etlStatus: ProcessStatus = {
        status: 'not_processed',
        message: 'Sin procesar'
    };

    steps = [
        { title: 'Vendedores y administradores', status: 'pending', progress: 0 },
        { title: 'Factor de comisiones de vendedores', status: 'pending', progress: 0 },
        { title: 'Factor de comisiones de administradores', status: 'pending', progress: 0 },
        { title: 'Ausentismos', status: 'pending', progress: 0 }
    ];
    stats = {
        totalVendedores: 0,
        totalComisiones: 0,
        totalComisionesJefes: 0,
        totalAusentismos: 0,
        totalEtl: 0
    };

    constructor(
        private fb: FormBuilder,
        private notificationHelper: NotificationHelper,
        private sincronizacionService: SincronizacionSapService,
        private periodosService: PeriodosmetasService
    ) {
        this.form = this.fb.group({
            periodo: ['', Validators.required]
        });
        // Inicializar el estado ETL en el constructor
        this.etlStatus = {
            status: 'not_processed',
            message: 'Sin procesar'
        };
    }

    ngOnInit(): void {
        this.cargarPeriodosAbiertos();
    }

    cargarPeriodosAbiertos(): void {
        showSpinner();
        this.periodosService.getAllPeriodos('JA')
            .pipe(finalize(() => hideSpinner()))
            .subscribe({
                next: (response: any) => {
                    if (Array.isArray(response)) {
                        // Filtrar solo los periodos abiertos
                        this.periodosAbiertos = response
                            .filter((periodo: any) => periodo.estadoperiodo === 'ABIERTO')
                            .map((periodo: any) => ({
                                ...periodo
                            }));
                    } else if (response && response.data && Array.isArray(response.data)) {
                        // Filtrar solo los periodos abiertos
                        this.periodosAbiertos = response.data
                            .filter((periodo: any) => periodo.estadoperiodo === 'ABIERTO')
                            .map((periodo: any) => ({
                                ...periodo
                            }));
                    } else {
                        this.periodosAbiertos = [];
                    }
                },
                error: (error: any) => {
                    this.notificationHelper.errorMessage('Error al cargar los periodos: ' + error.message);
                    this.periodosAbiertos = [];
                }
            });
    }

    async sincronizar(): Promise<void> {
        // Reset estado ETL
        this.etlStatus = {
            status: 'processing',
            message: 'Procesando...'
        };

        if (this.form.invalid) {
            this.notificationHelper.errorMessage('Por favor, seleccione un periodo');
            return;
        }

        this.isLoading = true;
        const periodo = this.form.get('periodo')?.value;

        try {
            // Paso 1: Consulta de Vendedores
            showSpinner('Procesando vendedores y administradores...');
            this.loadingStates.vendedores = true;
            this.updateStepStatus(0, 'in-progress');
            try {
                const vendedoresResponse = await this.sincronizacionService.consultarVendedores(periodo).toPromise();
                this.stats.totalVendedores = vendedoresResponse.inserted_count || 0;
                this.updateStepStatus(0, 'completed');
                this.loadingStates.vendedores = false;
            } catch (error: any) {
                this.updateStepStatus(0, 'error');
                this.loadingStates.vendedores = false;
                
                let errorMsg = 'Error al procesar vendedores';
                
                // Manejo especial para el error de validación de vendedores
                if (error.status === 400 && error.error?.detail?.message) {
                    // Es un error de validación de campos - usar el mensaje tal como viene del backend
                    errorMsg = error.error.detail.message;
                    
                    // Ya no mostraremos ejemplos de registros inválidos, solo el mensaje con el número total
                } else if (error.error?.detail) {
                    errorMsg += `: ${error.error.detail}`;
                } else if (error.message) {
                    errorMsg += `: ${error.message}`;
                }
                
                this.notificationHelper.errorMessage(errorMsg);
                error.handled = true; // Marcar como manejado para evitar mensajes duplicados
                throw error;
            }

            // Paso 2: Consulta de Comisionamiento
            showSpinner('Procesando factor de comisiones de vendedores...');
            this.loadingStates.comisiones = true;
            this.updateStepStatus(1, 'in-progress');
            try {
                const comisionamientoResponse = await this.sincronizacionService.consultarComisionamiento(periodo).toPromise();
                this.stats.totalComisiones = comisionamientoResponse.inserted_count || 0;
                this.updateStepStatus(1, 'completed');
                this.loadingStates.comisiones = false;
            } catch (error: any) {
                this.updateStepStatus(1, 'error');
                this.loadingStates.comisiones = false;
                let errorMsg = 'Error al procesar comisiones de vendedores';
                if (error.error?.detail) {
                    errorMsg += `: ${error.error.detail}`;
                } else if (error.message) {
                    errorMsg += `: ${error.message}`;
                }
                this.notificationHelper.errorMessage(errorMsg);
                throw error;
            }

            // Paso 3: Consulta de Comisionamiento Jefes
            showSpinner('Procesando factor de comisiones de administradores...');
            this.loadingStates.comisionesJefes = true;
            this.updateStepStatus(2, 'in-progress');
            try {
                const comisionamientoJefesResponse = await this.sincronizacionService.consultarComisionamientoJefe(periodo).toPromise();
                this.stats.totalComisionesJefes = comisionamientoJefesResponse.inserted_count || 0;
            } catch (error: any) {
                this.updateStepStatus(2, 'error');
                this.loadingStates.comisionesJefes = false;
                let errorMsg = 'Error al procesar comisiones de administradores';
                if (error.error?.detail) {
                    errorMsg += `: ${error.error.detail}`;
                } else if (error.message) {
                    errorMsg += `: ${error.message}`;
                }
                this.notificationHelper.errorMessage(errorMsg);
                throw error;
            }
            this.updateStepStatus(2, 'completed');
            this.loadingStates.comisionesJefes = false;

            // Paso 4: Consulta de Ausentismo
            showSpinner('Procesando ausentismos...');
            this.loadingStates.ausentismos = true;
            this.updateStepStatus(3, 'in-progress');
            const ausentismoResponse = await this.sincronizacionService.consultarAusentismo(periodo).toPromise();
            this.stats.totalAusentismos = ausentismoResponse.inserted_count || 0;
            this.updateStepStatus(3, 'completed');
            this.loadingStates.ausentismos = false;

            // Paso 5: Ejecutar Liquidación
            showSpinner('Ejecutando liquidación...');
            try {
                const liquidacionResponse = await this.sincronizacionService.ejecutarLiquidacion(periodo).toPromise();
                if (liquidacionResponse.success) {
                    // Actualizar estado ETL
                    this.etlStatus = {
                        status: 'processed',
                        message: 'Procesado'
                    };
                    hideSpinner();
                    this.notificationHelper.successMessage('Sincronización y liquidación completadas exitosamente');
                } else {
                    throw new Error('Error en la liquidación');
                }
            } catch (error: any) {
                this.etlStatus = {
                    status: 'error',
                    message: 'Error en liquidación'
                };
                hideSpinner();
                let errorMsg = 'Error al ejecutar la liquidación';
                if (error.error?.detail) {
                    errorMsg += `: ${error.error.detail}`;
                } else if (error.message) {
                    errorMsg += `: ${error.message}`;
                }
                this.notificationHelper.errorMessage(errorMsg);
                throw error;
            }
        } catch (error: any) {
            hideSpinner();
            
            // No mostrar mensaje aquí si ya se ha mostrado un mensaje específico en los bloques catch anteriores
            if (!error.handled) {
                // Personalizar el mensaje de error según el tipo
                let errorMsg = 'Error durante la sincronización';
                
                if (error.status === 400 && error.error?.detail?.message) {
                    errorMsg = error.error.detail.message;
                } else if (error.error?.detail) {
                    errorMsg += `: ${error.error.detail}`;
                } else if (error.message) {
                    errorMsg += `: ${error.message}`;
                } else {
                    errorMsg += ': Error desconocido';
                }
                
                this.notificationHelper.errorMessage(errorMsg);
            }
            
            this.updateStepStatus(this.currentStep, 'error');
            this.etlStatus = {
                status: 'error',
                message: 'No procesado'
            };
        } finally {
            this.isLoading = false;
            Object.keys(this.loadingStates).forEach(key => {
                this.loadingStates[key] = false;
            });
        }
    }

    private updateStepStatus(stepIndex: number, status: 'pending' | 'in-progress' | 'completed' | 'error'): void {
        this.steps[stepIndex].status = status;
        if (status === 'in-progress') {
            this.currentStep = stepIndex;
        }
        this.steps[stepIndex].progress = status === 'completed' ? 100 : 0;
    }
}
